import React, { Fragment, lazy, Suspense, useEffect, useState } from "react";
import { init, loadRemote } from "@module-federation/runtime";
import ReactDOM from "react-dom";
import { Box, Button, Grid, MenuItem, Select, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOutletContext } from "react-router-dom";
import { get } from "lodash";
import LoadingScreen from "./loadingScreen";
import { InitializeRemoteApp } from "./InitializeRemoteApp";

// Component to dynamically load and render a remote module
const RemoteComponent: React.FC<{ request: string | null; props?: Record<string, any> }> = ({ request, props }) => {
  const [error, setError] = useState<string | null>(null);
  const [Component, setComponent] = useState<React.LazyExoticComponent<any> | null>(null);

  useEffect(() => {
    let isMounted = true; // Track if the component is still mounted

    if (!request) {
      setError("No app loaded");
      return;
    }

    const loadComponent = async () => {
      try {
        const module: any = await loadRemote(request);
        if (isMounted) {
          setError(null); // Clear any previous errors
          setComponent(() => lazy(() => Promise.resolve({ default: module.default || module })));
        }
      } catch (error) {
        console.error("Failed to load remote module:", error);
        if (isMounted) {
          setError("Failed to load the selected app. Please check your options.");
        }
      }
    };

    loadComponent();

    // Cleanup function to reset state on unmount
    return () => {
      isMounted = false;
      setComponent(null);
      setError(null);
    };
  }, [request]);

  if (error) {
    return (
      <Typography variant="h6" color="error">
        {error}
      </Typography>
    );
  }

  if (!Component) {
    return <LoadingScreen />;
  }

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

const RemoteAppLoader: React.FC = () => {
  const { remoteUrl, remoteScope, remoteEntry }: any = get(useOutletContext(), 'customAppConfig', {});

  const theme = useTheme(); // Access the custom theme
  const [remoteModule, setRemoteModule] = useState<string | null>(null);
  const [remoteProps, setRemoteProps] = useState<Record<string, any>>({});
  const [isInitialized, setIsInitialized] = useState<boolean>(false); // Track initialization status

  // const [remoteUrl, setRemoteUrl] = useState<string>("http://localhost:3001");
  // const [remoteScope, setRemoteScope] = useState<string>("micro_app_1");
  // const [remoteEntry, setRemoteEntry] = useState<string>("App");
  
  const handleRemoteEvent = (data: string) => {
    console.log("Event from remote app:", data);
  };

  useEffect(() => {
    const initializeApp = async () => {
      if (remoteUrl && remoteScope) {
        try {          
          await InitializeRemoteApp(remoteUrl, remoteScope);
          setIsInitialized(true); // Mark initialization as complete
        } catch (error) {                    
          setTimeout(() => initializeApp(), 3000); // Retry after 3 seconds
        }
      }
    };

    initializeApp();
  }, [remoteUrl, remoteScope]);

  useEffect(() => {
    if (isInitialized && remoteUrl && remoteScope && remoteEntry) {
      loadRemoteApp();
    }
  }, [isInitialized, remoteUrl, remoteScope, remoteEntry]);

  const loadRemoteApp = async () => {
    if (!remoteUrl || !remoteScope || !remoteEntry) {      
      return;
    }

    try {      
      setRemoteModule(`${remoteScope}/${remoteEntry}`);
      setRemoteProps({
        parentName: `Dynamic ${remoteEntry} Widget`,
        someProp: `This is a dynamically loaded ${remoteEntry}`,
        onRemoteEvent: handleRemoteEvent,
      });
    } catch (error) {          
      setTimeout(() => loadRemoteApp(), 3000); // Retry after 3 seconds
    }
  };
  
  return (
    <Fragment>
      {remoteModule ? (
        <RemoteComponent request={remoteModule} props={remoteProps} />
      ) : (
        <Typography variant="h6">Loading remote app...</Typography>
      )}
    </Fragment>
  );
};

export default RemoteAppLoader;